/**
 * My Featured Tracks Page
 * Shows artist's scheduled, active, and ending featured tracks
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  SimpleGrid,
  Icon,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel
} from '@chakra-ui/react';
import { FiClock, FiCalendar, FiTrendingUp } from 'react-icons/fi';
import { format } from 'date-fns';
import { useFeaturedTracks } from '../../hooks/useFeaturedTracks';
import { useCreditContext } from '../../context/CreditContext';
import CreditBalanceWidget from './CreditBalanceWidget';
import PurchaseCreditsModal from './PurchaseCreditsModal';

const MyFeaturedTracks = () => {
  const { myFeaturedTracks, loading, fetchMyFeaturedTracks, extendFeature, cancelFeature } = useFeaturedTracks();
  const { credits } = useCreditContext();
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [extendAmount, setExtendAmount] = useState(24);

  // Modal controls
  const { isOpen: isPurchaseOpen, onOpen: onPurchaseOpen, onClose: onPurchaseClose } = useDisclosure();
  const { isOpen: isExtendOpen, onOpen: onExtendOpen, onClose: onExtendClose } = useDisclosure();

  useEffect(() => {
    fetchMyFeaturedTracks();
  }, [fetchMyFeaturedTracks]);

  // Get status badge
  const getStatusBadge = (status, isScheduled, isActiveNow) => {
    if (status === 'scheduled') {
      return <Badge bg="secondary.500" color="orange" px={2} py={1} borderRadius="sm">Scheduled</Badge>;
    } else if (status === 'active' || isActiveNow) {
      return <Badge bg="secondary.500" color="green.400" px={2} py={1} borderRadius="sm">Live</Badge>;
    } else {
      return <Badge bg="secondary.500" color="red.400" px={2} py={1} borderRadius="sm">Ended</Badge>;
    }
  };

  // Handle extend
  const handleExtend = (track) => {
    setSelectedTrack(track);
    setExtendAmount(24);
    onExtendOpen();
  };

  // Confirm extend
  const confirmExtend = async () => {
    if (!selectedTrack) return;

    try {
      await extendFeature(selectedTrack.track_id, extendAmount);
      onExtendClose();
      fetchMyFeaturedTracks();
    } catch (error) {
      console.error('Extend failed:', error);
    }
  };

  // Handle cancel
  const handleCancel = async (trackId, trackName) => {
    if (window.confirm(`Are you sure you want to cancel the feature for "${trackName}"? Credits are not refunded.`)) {
      try {
        await cancelFeature(trackId);
        fetchMyFeaturedTracks();
      } catch (error) {
        console.error('Cancel failed:', error);
      }
    }
  };

  if (loading && myFeaturedTracks.length === 0) {
    return (
      <Box p={{ base: 4, md: 8 }}>
        <VStack spacing={4}>
          <Spinner size="xl" color="dark.900" />
          <Text color="primary.900">Loading your featured tracks...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 8 }}>
      <VStack spacing={8} align="stretch">
        {/* Page Header */}
        <Heading size="lg" color="dark.900">My Featured Tracks</Heading>

        {/* Credit Balance */}
        <CreditBalanceWidget
          onPurchaseClick={onPurchaseOpen}
          showDetails={true}
        />

        {/* Featured Tracks List */}
        {myFeaturedTracks.length === 0 ? (
          <Box
            bg="secondary.100"
            border="1px solid"
            borderColor="secondary.500"
            borderRadius="md"
            p={6}
          >
            <Text fontWeight="bold" color="dark.900" mb={1}>No Featured Tracks</Text>
            <Text fontSize="sm" color="primary.900">
              You haven't featured any tracks yet. Go to your catalogue and feature a track to get started!
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {myFeaturedTracks.map((feature) => (
              <Box
                key={feature.feature_id}
                bg="dark.500"
                borderRadius="md"
                boxShadow="2xl"
                p={5}
              >
                <VStack align="stretch" spacing={4}>
                  {/* Track Name & Status */}
                  <HStack justify="space-between">
                    <Text fontWeight="bold" fontSize="lg" color="primary.100" noOfLines={1}>
                      {feature.track_name}
                    </Text>
                    {getStatusBadge(feature.status, feature.is_scheduled, feature.is_active_now)}
                  </HStack>

                  {/* Timing Info */}
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <Icon as={FiCalendar} color="primary.900" />
                      <Text fontSize="sm" color="primary.900">
                        Starts: {format(new Date(feature.started_at), 'MMM d, h:mm a')}
                      </Text>
                    </HStack>

                    <HStack>
                      <Icon as={FiClock} color="primary.900" />
                      <Text fontSize="sm" color="primary.900">
                        Ends: {format(new Date(feature.expires_at), 'MMM d, h:mm a')}
                      </Text>
                    </HStack>

                    {feature.is_scheduled && feature.hours_until_start > 0 && (
                      <HStack>
                        <Icon as={FiTrendingUp} color="orange" />
                        <Text fontSize="sm" color="orange" fontWeight="medium">
                          Starts in {Math.round(feature.hours_until_start)} hours
                        </Text>
                      </HStack>
                    )}

                    {feature.is_active_now && feature.hours_remaining > 0 && (
                      <HStack>
                        <Icon as={FiTrendingUp} color="green.400" />
                        <Text fontSize="sm" color="green.400" fontWeight="medium">
                          {Math.round(feature.hours_remaining)} hours remaining
                        </Text>
                      </HStack>
                    )}
                  </VStack>

                  {/* Credits Info */}
                  <Box
                    bg="secondary.100"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="secondary.500"
                  >
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium" color="primary.900">Credits Spent:</Text>
                      <Text fontSize="sm" fontWeight="bold" color="dark.900">
                        {feature.credits_spent} credits
                      </Text>
                    </HStack>
                  </Box>

                  {/* Actions */}
                  {(feature.is_scheduled || feature.is_active_now) && (
                    <HStack spacing={2}>
                      {feature.is_active_now && (
                        <Button
                          size="sm"
                          variant="glassy"
                          onClick={() => handleExtend(feature)}
                          flex={1}
                        >
                          Extend
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        color="red.400"
                        _hover={{ bg: 'secondary.500', color: 'red.300' }}
                        onClick={() => handleCancel(feature.track_id, feature.track_name)}
                        flex={1}
                      >
                        Cancel
                      </Button>
                    </HStack>
                  )}
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </VStack>

      {/* Purchase Credits Modal */}
      <PurchaseCreditsModal
        isOpen={isPurchaseOpen}
        onClose={onPurchaseClose}
      />

      {/* Extend Feature Modal */}
      <Modal isOpen={isExtendOpen} onClose={onExtendClose}>
        <ModalOverlay />
        <ModalContent bg="secondary.100" color="primary.100">
          <ModalHeader bg="secondary.100" color="dark.900" borderBottom="1px solid" borderColor="secondary.500">
            Extend Feature
          </ModalHeader>
          <ModalCloseButton color="primary.900" />
          <ModalBody bg="secondary.100" py={6}>
            <VStack spacing={4} align="stretch">
              <Text color="primary.900">
                How many additional hours would you like to add to{' '}
                <Text as="span" fontWeight="bold" color="dark.900">{selectedTrack?.track_name}</Text>?
              </Text>

              <FormControl>
                <FormLabel color="primary.900">Additional Hours</FormLabel>
                <NumberInput
                  value={extendAmount}
                  onChange={(_, value) => setExtendAmount(value)}
                  min={1}
                  max={720}
                >
                  <NumberInputField
                    bg="secondary.500"
                    border="1px solid"
                    borderColor="secondary.700"
                    color="primary.100"
                    _focus={{ borderColor: 'dark.900' }}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper color="primary.900" />
                    <NumberDecrementStepper color="primary.900" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <Box bg="dark.500" p={3} borderRadius="md">
                <VStack align="stretch" spacing={1}>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="primary.900">Cost:</Text>
                    <Text fontSize="sm" fontWeight="bold" color="dark.900">
                      {extendAmount} credits (${(extendAmount * 1.00).toFixed(2)})
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="primary.900">Available:</Text>
                    <Text fontSize="sm" color="primary.100">{credits?.available_credits || 0} credits</Text>
                  </HStack>
                </VStack>
              </Box>

              {credits && credits.available_credits < extendAmount && (
                <Box bg="secondary.500" border="1px solid" borderColor="red.400" borderRadius="md" p={3}>
                  <Text fontSize="sm" color="red.400">
                    Insufficient credits. Purchase more to continue.
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter bg="secondary.100" borderTop="1px solid" borderColor="secondary.500">
            <Button variant="ghost" mr={3} onClick={onExtendClose}>
              Cancel
            </Button>
            <Button
              variant="glassy"
              onClick={confirmExtend}
              isDisabled={!credits || credits.available_credits < extendAmount}
            >
              Extend for {extendAmount} hours
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MyFeaturedTracks;
